import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { rejectParticipantAudio, rejectParticipantVideo } from '../../../av-moderation/actions';
import participantsPaneTheme from '../../../base/components/themes/participantsPaneTheme.json';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getParticipantById, isLocalParticipantModerator } from '../../../base/participants/functions';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import useContextMenu from '../../../base/ui/hooks/useContextMenu.web';
import { normalizeAccents } from '../../../base/util/strings.web';
import { getBreakoutRooms, getCurrentRoomId, isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { isButtonEnabled, showOverflowDrawer } from '../../../toolbox/functions.web';
import { muteRemote } from '../../../video-menu/actions.web';
import { getSortedCasCadeRoomIds, getSortedParticipantIds, isCurrentRoomRenamable, shouldRenderInviteButton } from '../../functions';
import { useParticipantDrawer } from '../../hooks';
import MeetingParticipantContextMenu from './MeetingParticipantContextMenu';
import MeetingParticipantItems from './MeetingParticipantItems';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp, IconClear } from '../../../base/icons/svg';
import { changeDisplayParticipant } from '../../actions.any';
import { getKnockingParticipants } from '../../../lobby/functions';
import { InputAdornment, TextField } from '@mui/material';
import ClickableIcon from '../../../base/ui/components/web/ClickableIcon';
import { IconSearchParticipant } from '../../../moderator-controller/components/ModeratorControllerIcons';
import { countCascadeRaiseHands } from '../../../raisehand-moderation/functions';
const useStyles = makeStyles()(theme => {
    return {
        headingW: {
            color: theme.palette.warning02
        },
        heading: {
            color: theme.palette.text02,
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            marginBottom: theme.spacing(3),
            [`@media(max-width: ${participantsPaneTheme.MD_BREAKPOINT})`]: {
                ...withPixelLineHeight(theme.typography.bodyShortBoldLarge)
            }
        },
        search: {
            margin: `0 0 ${theme.spacing(3)} 0`,
            '& input': {
                // textAlign: 'center',
                paddingRight: '16px'
            },
            top: '10px',
            "& svg": {
                fill: "#73777A !important"
            }
        },
        participant: {
            color: theme.palette.default00,
            fontSize: '16px',
            fontWeight: 700
        }
    };
});
/**
 * Renders the MeetingParticipantList component.
 * NOTE: This component is not using useSelector on purpose. The child components MeetingParticipantItem
 * and MeetingParticipantContextMenu are using connect. Having those mixed leads to problems.
 * When this one was using useSelector and the other two were not -the other two were re-rendered before this one was
 * re-rendered, so when participant is leaving, we first re-render the item and menu components,
 * throwing errors (closing the page) before removing those components for the participant that left.
 *
 * @returns {ReactNode} - The component.
 */
function MeetingParticipants({ currentRoom, overflowDrawer, participantsCount, searchString, setSearchString, showInviteButton, sortedParticipantIds = [], sortedCascadeRoomIds = [], leftParentElRef }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const participants = useSelector(getKnockingParticipants);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const { raisedHandsQueue } = useSelector((state) => state['features/base/participants']);
    const countAllCascadeRaisedHandQueue = useSelector((state) => countCascadeRaiseHands(state) || 0);
    const totalRaisedHand = raisedHandsQueue?.length + countAllCascadeRaisedHandQueue;
    const { tabLeftParticipant, isDisplayParticipated, isDisplayLobby } = useSelector((state) => state['features/participants-pane']);
    const [lowerMenu, , toggleMenu, menuEnter, menuLeave, raiseContext] = useContextMenu();
    const muteAudio = useCallback(id => () => {
        dispatch(muteRemote(id, MEDIA_TYPE.AUDIO));
        dispatch(rejectParticipantAudio(id));
    }, [dispatch]);
    const stopVideo = useCallback(id => () => {
        dispatch(muteRemote(id, MEDIA_TYPE.VIDEO));
        dispatch(rejectParticipantVideo(id));
    }, [dispatch]);
    const [drawerParticipant, closeDrawer, openDrawerForParticipant] = useParticipantDrawer();
    const [displayNormalParticipant, setDisplayNormalParticipant] = useState(true);
    const [displaySipParticipant, setDisplaySipParticipant] = useState(false);
    // FIXME:
    // It seems that useTranslation is not very scalable. Unmount 500 components that have the useTranslation hook is
    // taking more than 10s. To workaround the issue we need to pass the texts as props. This is temporary and dirty
    // solution!!!
    // One potential proper fix would be to use react-window component in order to lower the number of components
    // mounted.
    const participantActionEllipsisLabel = t('participantsPane.actions.moreParticipantOptions');
    const youText = t('chat.you');
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const _isCurrentRoomRenamable = useSelector(isCurrentRoomRenamable);
    const { classes: styles } = useStyles();
    const closeParticipane = () => {
        dispatch(changeDisplayParticipant(false));
    };
    const openParticipane = () => {
        dispatch(changeDisplayParticipant(true));
    };
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    // useEffect(() => {
    //     if(raisedHandsQueue?.length === 0){
    //         openParticipane();
    //     }
    // }, [raisedHandsQueue?.length])
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: 'center', height: (participants?.length > 0 || totalRaisedHand > 0) ? '48px' : '0px' } },
            (participants?.length > 0 || totalRaisedHand > 0) ? (React.createElement("div", { className: styles.participant }, t("participantsPane.headings.participated", { count: sortedParticipantIds.length + sortedCascadeRoomIds.length }))) : null,
            (participants?.length > 0 || totalRaisedHand > 0) ? (isDisplayParticipated ? (React.createElement(Icon, { onClick: () => closeParticipane(), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowUp, size: 18, color: "#44494D" })) : (React.createElement(Icon, { onClick: () => openParticipane(), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowDown, size: 18, color: "#44494D" }))) : null),
        isDisplayParticipated ? (React.createElement("div", null,
            React.createElement(TextField, { "data-size": 'normal', autoFocus: false, fullWidth: true, placeholder: t("participantsPane.search"), value: searchString, onChange: (e) => setSearchString(e.target.value), InputProps: {
                    startAdornment: (React.createElement(InputAdornment, { position: 'start' },
                        React.createElement(IconSearchParticipant, null))),
                    endAdornment: (React.createElement(React.Fragment, null, searchString.length > 0 &&
                        React.createElement(InputAdornment, { position: 'end' },
                            React.createElement(ClickableIcon, { accessibilityLabel: 'clear-input', icon: IconClear, onClick: () => setSearchString(''), className: 'icon-input', onKeyPress: (e) => {
                                    if (e?.key === 'Enter')
                                        setSearchString('');
                                } }))))
                } }),
            React.createElement("div", null,
                React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: '20px 0 14px 0', alignItems: 'center' } },
                    React.createElement("div", { className: styles.participant }, t("participantsPane.headings.sipParticipant", { count: sortedCascadeRoomIds.length })),
                    sortedCascadeRoomIds.length > 0 && ((displaySipParticipant) ? (React.createElement(Icon, { onClick: () => setDisplaySipParticipant(false), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowUp, size: 18, color: "#44494D" })) : (React.createElement(Icon, { onClick: () => setDisplaySipParticipant(true), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowDown, size: 18, color: "#44494D" })))),
                displaySipParticipant &&
                    React.createElement(MeetingParticipantItems, { handleClick: handleClick, isInBreakoutRoom: isBreakoutRoom, lowerMenu: lowerMenu, muteAudio: muteAudio, openDrawerForParticipant: openDrawerForParticipant, overflowDrawer: false, participantActionEllipsisLabel: participantActionEllipsisLabel, participantIds: sortedCascadeRoomIds, raiseContextId: raiseContext.entity, searchString: normalizeAccents(searchString), stopVideo: stopVideo, toggleMenu: toggleMenu, youText: youText, leftParentElRef: leftParentElRef, percentDisplayVirtualList: displayNormalParticipant ? 30 : 45 })),
            React.createElement(MeetingParticipantContextMenu, { closeDrawer: closeDrawer, drawerParticipant: drawerParticipant, muteAudio: muteAudio, offsetTarget: raiseContext?.offsetTarget, onEnter: menuEnter, onLeave: menuLeave, onSelect: lowerMenu, overflowDrawer: false, participantID: raiseContext?.entity, anchorEl: anchorEl, open: open, handleClose: handleClose, useMuiMenu: true }),
            React.createElement("div", null,
                React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: '14px 0', alignItems: 'center' } },
                    React.createElement("div", { className: styles.participant }, t("participantsPane.headings.normalParticipant", { count: sortedParticipantIds.length })),
                    (displayNormalParticipant ? (React.createElement(Icon, { onClick: () => setDisplayNormalParticipant(false), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowUp, size: 18, color: "#44494D" })) : (React.createElement(Icon, { onClick: () => setDisplayNormalParticipant(true), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowDown, size: 18, color: "#44494D" })))),
                displayNormalParticipant &&
                    React.createElement(MeetingParticipantItems, { handleClick: handleClick, isInBreakoutRoom: isBreakoutRoom, lowerMenu: lowerMenu, muteAudio: muteAudio, openDrawerForParticipant: openDrawerForParticipant, overflowDrawer: false, participantActionEllipsisLabel: participantActionEllipsisLabel, participantIds: sortedParticipantIds, raiseContextId: raiseContext.entity, searchString: normalizeAccents(searchString), stopVideo: stopVideo, toggleMenu: toggleMenu, youText: youText, leftParentElRef: leftParentElRef, percentDisplayVirtualList: displaySipParticipant ? 30 : 70 })),
            React.createElement(MeetingParticipantContextMenu, { closeDrawer: closeDrawer, drawerParticipant: drawerParticipant, muteAudio: muteAudio, offsetTarget: raiseContext?.offsetTarget, onEnter: menuEnter, onLeave: menuLeave, onSelect: lowerMenu, overflowDrawer: false, participantID: raiseContext?.entity, anchorEl: anchorEl, open: open, handleClose: handleClose, useMuiMenu: true }))) : null));
}
/**
 * Maps (parts of) the redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    let sortedParticipantIds = getSortedParticipantIds(state);
    let sortedCascadeRoomIds = getSortedCasCadeRoomIds(state);
    // Filter out the virtual screenshare participants since we do not want them to be displayed as separate
    // participants in the participants pane.
    sortedParticipantIds = sortedParticipantIds.filter((id) => {
        const participant = getParticipantById(state, id);
        // return !isScreenShareParticipant(participant);
        return participant;
    });
    sortedCascadeRoomIds = sortedCascadeRoomIds.filter((id) => {
        const participant = getParticipantById(state, id);
        // return !isScreenShareParticipant(participant);
        return participant;
    });
    const participantsCount = sortedParticipantIds.length;
    const cascadeRoomCount = sortedCascadeRoomIds.length;
    const showInviteButton = shouldRenderInviteButton(state) && isButtonEnabled('invite', state);
    const overflowDrawer = showOverflowDrawer(state);
    const currentRoomId = getCurrentRoomId(state);
    const currentRoom = getBreakoutRooms(state)[currentRoomId];
    const isLocalModerator = isLocalParticipantModerator(state);
    return {
        currentRoom,
        overflowDrawer,
        participantsCount,
        showInviteButton,
        sortedParticipantIds,
        sortedCascadeRoomIds,
        cascadeRoomCount
    };
}
export default connect(_mapStateToProps)(MeetingParticipants);
